import React, { Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, ScatterChart, Scatter, ZAxis } from 'recharts';
const MaterialRiskAlertsDashboard: React.FC = () => {
  const kpis = [{
    title: 'Shortage Alerts',
    value: '23',
    change: '+5',
    isPositive: false,
    subtitle: 'Active alerts'
  }, {
    title: 'High-Risk SKUs',
    value: '47',
    change: '+8',
    isPositive: false,
    subtitle: 'Critical attention needed'
  }, {
    title: 'Impact Cost',
    value: '₹185 Cr',
    change: '+12%',
    isPositive: false,
    subtitle: 'Potential financial impact'
  }];
  // Risk Heatmap Data
  const riskHeatmapData = [{
    category: 'Electronics',
    critical: 8,
    high: 12,
    medium: 18,
    low: 25,
    total: 63
  }, {
    category: 'Raw Materials',
    critical: 3,
    high: 8,
    medium: 22,
    low: 42,
    total: 75
  }, {
    category: 'Components',
    critical: 5,
    high: 15,
    medium: 28,
    low: 38,
    total: 86
  }, {
    category: 'Sub-Assemblies',
    critical: 4,
    high: 9,
    medium: 15,
    low: 28,
    total: 56
  }, {
    category: 'Packaging',
    critical: 1,
    high: 3,
    medium: 8,
    low: 18,
    total: 30
  }, {
    category: 'Consumables',
    critical: 2,
    high: 5,
    medium: 12,
    low: 23,
    total: 42
  }];
  // Shortage Trend Chart Data
  const shortageTrendData = [{
    month: 'Jan',
    critical: 5,
    high: 8,
    medium: 15,
    resolved: 12
  }, {
    month: 'Feb',
    critical: 6,
    high: 10,
    medium: 18,
    resolved: 15
  }, {
    month: 'Mar',
    critical: 7,
    high: 12,
    medium: 20,
    resolved: 18
  }, {
    month: 'Apr',
    critical: 6,
    high: 11,
    medium: 19,
    resolved: 16
  }, {
    month: 'May',
    critical: 8,
    high: 14,
    medium: 22,
    resolved: 20
  }, {
    month: 'Jun',
    critical: 7,
    high: 13,
    medium: 21,
    resolved: 19
  }, {
    month: 'Jul',
    critical: 9,
    high: 15,
    medium: 24,
    resolved: 22
  }, {
    month: 'Aug',
    critical: 8,
    high: 14,
    medium: 23,
    resolved: 21
  }, {
    month: 'Sep',
    critical: 8,
    high: 14,
    medium: 23,
    resolved: 23
  }];
  // SKU Dependency Network Graph Data
  const dependencyNetworkData = [
  // Critical SKUs with high dependencies
  {
    sku: 'ECU-2024-A',
    risk: 'Critical',
    dependencies: 8,
    suppliers: 2,
    impact: 95,
    x: 85,
    y: 90
  }, {
    sku: 'BATT-SYS-H',
    risk: 'Critical',
    dependencies: 7,
    suppliers: 1,
    impact: 92,
    x: 90,
    y: 85
  }, {
    sku: 'SENS-MOD-K',
    risk: 'Critical',
    dependencies: 6,
    suppliers: 2,
    impact: 88,
    x: 80,
    y: 95
  },
  // High Risk SKUs
  {
    sku: 'LED-HL-M',
    risk: 'High',
    dependencies: 5,
    suppliers: 3,
    impact: 75,
    x: 65,
    y: 70
  }, {
    sku: 'WIRE-HRN-K',
    risk: 'High',
    dependencies: 6,
    suppliers: 2,
    impact: 78,
    x: 70,
    y: 75
  }, {
    sku: 'HVAC-BLW-T',
    risk: 'High',
    dependencies: 4,
    suppliers: 3,
    impact: 72,
    x: 60,
    y: 65
  }, {
    sku: 'DOOR-HDL-M',
    risk: 'High',
    dependencies: 5,
    suppliers: 2,
    impact: 76,
    x: 68,
    y: 72
  },
  // Medium Risk SKUs
  {
    sku: 'INT-TRM-H',
    risk: 'Medium',
    dependencies: 3,
    suppliers: 4,
    impact: 55,
    x: 45,
    y: 50
  }, {
    sku: 'BRAK-PAD-V',
    risk: 'Medium',
    dependencies: 4,
    suppliers: 3,
    impact: 58,
    x: 50,
    y: 55
  }, {
    sku: 'FUEL-PMP-M',
    risk: 'Medium',
    dependencies: 3,
    suppliers: 4,
    impact: 52,
    x: 42,
    y: 48
  }, {
    sku: 'TAIL-LP-T',
    risk: 'Medium',
    dependencies: 4,
    suppliers: 3,
    impact: 56,
    x: 48,
    y: 52
  },
  // Low Risk SKUs
  {
    sku: 'SEAL-KIT-A',
    risk: 'Low',
    dependencies: 2,
    suppliers: 5,
    impact: 25,
    x: 20,
    y: 25
  }, {
    sku: 'BOLT-SET-B',
    risk: 'Low',
    dependencies: 1,
    suppliers: 6,
    impact: 18,
    x: 15,
    y: 20
  }, {
    sku: 'CLIP-ASM-C',
    risk: 'Low',
    dependencies: 2,
    suppliers: 5,
    impact: 22,
    x: 22,
    y: 28
  }];
  // Insecure SKUs Table Data
  const insecureSKUsData = [{
    sku: 'ECU-2024-A',
    description: 'Electronic Control Unit - Advanced',
    category: 'Electronics',
    riskLevel: 'Critical',
    shortageRisk: 'High',
    supplierCount: 2,
    leadTime: 45,
    currentStock: 125,
    safetyStock: 350,
    monthlyDemand: 285,
    daysToStockout: 15,
    impactCost: 42.5,
    mitigation: 'Emergency sourcing, alternate supplier qualification',
    affectedModels: 'Nexon EV, Tiago EV, Tigor EV'
  }, {
    sku: 'BATT-SYS-H',
    description: 'Battery Management System',
    category: 'Electronics',
    riskLevel: 'Critical',
    shortageRisk: 'High',
    supplierCount: 1,
    leadTime: 52,
    currentStock: 85,
    safetyStock: 280,
    monthlyDemand: 245,
    daysToStockout: 10,
    impactCost: 38.2,
    mitigation: 'Single source - urgent dual sourcing required',
    affectedModels: 'All EV models'
  }, {
    sku: 'SENS-MOD-K',
    description: 'Parking Sensor Module',
    category: 'Electronics',
    riskLevel: 'Critical',
    shortageRisk: 'Medium',
    supplierCount: 2,
    leadTime: 38,
    currentStock: 245,
    safetyStock: 420,
    monthlyDemand: 385,
    daysToStockout: 19,
    impactCost: 28.5,
    mitigation: 'Increase buffer stock, expedite shipments',
    affectedModels: 'Creta, Venue, i20, Verna'
  }, {
    sku: 'LED-HL-M',
    description: 'LED Headlamp Module',
    category: 'Components',
    riskLevel: 'High',
    shortageRisk: 'High',
    supplierCount: 3,
    leadTime: 32,
    currentStock: 385,
    safetyStock: 580,
    monthlyDemand: 485,
    daysToStockout: 24,
    impactCost: 22.8,
    mitigation: 'Supplier capacity expansion, alternate design',
    affectedModels: 'Swift, Baleno, Brezza'
  }, {
    sku: 'WIRE-HRN-K',
    description: 'Wiring Harness Assembly',
    category: 'Components',
    riskLevel: 'High',
    shortageRisk: 'Medium',
    supplierCount: 2,
    leadTime: 28,
    currentStock: 425,
    safetyStock: 620,
    monthlyDemand: 528,
    daysToStockout: 24,
    impactCost: 18.5,
    mitigation: 'Local sourcing initiatives, inventory buildup',
    affectedModels: 'Seltos, Sonet, Carens'
  }, {
    sku: 'HVAC-BLW-T',
    description: 'HVAC Blower Motor',
    category: 'Components',
    riskLevel: 'High',
    shortageRisk: 'Medium',
    supplierCount: 3,
    leadTime: 25,
    currentStock: 485,
    safetyStock: 680,
    monthlyDemand: 568,
    daysToStockout: 26,
    impactCost: 15.2,
    mitigation: 'Supplier performance improvement program',
    affectedModels: 'Innova, Fortuner, Hyryder'
  }, {
    sku: 'DOOR-HDL-M',
    description: 'Door Handle Assembly',
    category: 'Sub-Assemblies',
    riskLevel: 'High',
    shortageRisk: 'Medium',
    supplierCount: 2,
    leadTime: 22,
    currentStock: 528,
    safetyStock: 720,
    monthlyDemand: 625,
    daysToStockout: 25,
    impactCost: 12.8,
    mitigation: 'Expedited shipping, buffer stock increase',
    affectedModels: 'XUV700, Scorpio N, XUV300'
  }, {
    sku: 'INT-TRM-H',
    description: 'Interior Trim Panel',
    category: 'Sub-Assemblies',
    riskLevel: 'Medium',
    shortageRisk: 'Low',
    supplierCount: 4,
    leadTime: 18,
    currentStock: 685,
    safetyStock: 820,
    monthlyDemand: 728,
    daysToStockout: 28,
    impactCost: 8.5,
    mitigation: 'Routine monitoring, maintain current strategy',
    affectedModels: 'City, Amaze, Elevate'
  }, {
    sku: 'BRAK-PAD-V',
    description: 'Brake Pad Assembly',
    category: 'Components',
    riskLevel: 'Medium',
    shortageRisk: 'Low',
    supplierCount: 3,
    leadTime: 15,
    currentStock: 825,
    safetyStock: 950,
    monthlyDemand: 845,
    daysToStockout: 29,
    impactCost: 6.2,
    mitigation: 'Standard replenishment process',
    affectedModels: 'Taigun, Virtus, Kushaq'
  }, {
    sku: 'FUEL-PMP-M',
    description: 'Fuel Pump Module',
    category: 'Components',
    riskLevel: 'Medium',
    shortageRisk: 'Low',
    supplierCount: 4,
    leadTime: 20,
    currentStock: 725,
    safetyStock: 880,
    monthlyDemand: 785,
    daysToStockout: 28,
    impactCost: 5.8,
    mitigation: 'Monitor supplier capacity utilization',
    affectedModels: 'Hector, Astor, ZS EV'
  }];
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Critical':
        return 'bg-red-500';
      case 'High':
        return 'bg-orange-500';
      case 'Medium':
        return 'bg-yellow-500';
      case 'Low':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };
  const getRiskTextColor = (risk: string) => {
    switch (risk) {
      case 'Critical':
        return 'text-red-600 bg-red-50 border-red-300';
      case 'High':
        return 'text-orange-600 bg-orange-50 border-orange-300';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-300';
      case 'Low':
        return 'text-green-600 bg-green-50 border-green-300';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-300';
    }
  };
  const getShortageRiskColor = (risk: string) => {
    switch (risk) {
      case 'High':
        return 'text-red-600 bg-red-100';
      case 'Medium':
        return 'text-orange-600 bg-orange-100';
      case 'Low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };
  const getScatterColor = (risk: string) => {
    switch (risk) {
      case 'Critical':
        return '#ef4444';
      case 'High':
        return '#f97316';
      case 'Medium':
        return '#eab308';
      case 'Low':
        return '#22c55e';
      default:
        return '#6b7280';
    }
  };
  return <div data-id="element-9603">
      <DescriptionPanel title="Material Shortage & Risk Alerts" description="Real-time monitoring of material shortages, supply chain risks, and SKU dependencies. Track critical alerts, assess financial impact, and implement mitigation strategies to ensure uninterrupted production and minimize supply chain disruptions." insights={['23 active shortage alerts - 8 critical SKUs require immediate attention and mitigation', '47 high-risk SKUs identified - total potential impact of ₹185 Cr if not addressed', 'Electronics category showing highest risk with 8 critical and 12 high-risk items', 'Single-source dependencies identified for 12 critical SKUs - dual sourcing initiatives underway']} data-id="element-9604" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-9605">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-9606">
          Material Risk Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9607">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-9608">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-9609">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-9610">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-9611">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-9612">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-9613" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-9614" />}
                {kpi.change} vs last month
              </div>
            </Card>)}
        </div>
      </div>
      {/* Risk Heatmap and Shortage Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-9615">
        {/* Risk Heatmap */}
        <Card className="p-6" data-id="element-9616">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9617">
            Risk Heatmap by Category
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9618">
            Distribution of risk levels across material categories
          </p>
          <div className="space-y-4" data-id="element-9619">
            {riskHeatmapData.map((row, index) => <div key={index} data-id="element-9620">
                <div className="flex items-center justify-between mb-2" data-id="element-9621">
                  <span className="text-sm font-semibold text-gray-800" data-id="element-9622">
                    {row.category}
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-9623">
                    Total: {row.total} SKUs
                  </span>
                </div>
                <div className="flex h-10 rounded-lg overflow-hidden" data-id="element-9624">
                  <div className="bg-red-500 flex items-center justify-center text-white text-xs font-bold" style={{
                width: `${row.critical / row.total * 100}%`
              }} data-id="element-9625">
                    {row.critical > 0 && row.critical}
                  </div>
                  <div className="bg-orange-500 flex items-center justify-center text-white text-xs font-bold" style={{
                width: `${row.high / row.total * 100}%`
              }} data-id="element-9626">
                    {row.high > 0 && row.high}
                  </div>
                  <div className="bg-yellow-500 flex items-center justify-center text-white text-xs font-bold" style={{
                width: `${row.medium / row.total * 100}%`
              }} data-id="element-9627">
                    {row.medium > 0 && row.medium}
                  </div>
                  <div className="bg-green-500 flex items-center justify-center text-white text-xs font-bold" style={{
                width: `${row.low / row.total * 100}%`
              }} data-id="element-9628">
                    {row.low > 0 && row.low}
                  </div>
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500" data-id="element-9629">
                  <span data-id="element-9630">Critical: {row.critical}</span>
                  <span data-id="element-9631">High: {row.high}</span>
                  <span data-id="element-9632">Medium: {row.medium}</span>
                  <span data-id="element-9633">Low: {row.low}</span>
                </div>
              </div>)}
          </div>
          <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-9634">
            <div className="flex items-center justify-between text-xs" data-id="element-9635">
              <span className="text-gray-600" data-id="element-9636">Risk Levels:</span>
              <div className="flex items-center space-x-3" data-id="element-9637">
                <div className="flex items-center" data-id="element-9638">
                  <div className="w-3 h-3 rounded bg-red-500 mr-1" data-id="element-9639" />
                  <span data-id="element-9640">Critical</span>
                </div>
                <div className="flex items-center" data-id="element-9641">
                  <div className="w-3 h-3 rounded bg-orange-500 mr-1" data-id="element-9642" />
                  <span data-id="element-9643">High</span>
                </div>
                <div className="flex items-center" data-id="element-9644">
                  <div className="w-3 h-3 rounded bg-yellow-500 mr-1" data-id="element-9645" />
                  <span data-id="element-9646">Medium</span>
                </div>
                <div className="flex items-center" data-id="element-9647">
                  <div className="w-3 h-3 rounded bg-green-500 mr-1" data-id="element-9648" />
                  <span data-id="element-9649">Low</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
        {/* Shortage Trend Chart */}
        <Card className="p-6" data-id="element-9650">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9651">
            Shortage Trend Chart
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9652">
            Monthly shortage alerts by severity level
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-9653">
            <ComposedChart data={shortageTrendData} data-id="element-9654">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9655" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-9656" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-9657" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-9658" />
              <Legend data-id="element-9659" />
              <Bar dataKey="critical" stackId="a" fill="#ef4444" name="Critical" data-id="element-9660" />
              <Bar dataKey="high" stackId="a" fill="#f97316" name="High" data-id="element-9661" />
              <Bar dataKey="medium" stackId="a" fill="#eab308" name="Medium" data-id="element-9662" />
              <Line type="monotone" dataKey="resolved" stroke="#22c55e" strokeWidth={3} name="Resolved" dot={{
              r: 5
            }} data-id="element-9663" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9664">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-9665">
              <div className="text-center" data-id="element-9666">
                <div className="text-gray-600" data-id="element-9667">Critical</div>
                <div className="text-xl font-bold text-red-600" data-id="element-9668">8</div>
              </div>
              <div className="text-center" data-id="element-9669">
                <div className="text-gray-600" data-id="element-9670">High</div>
                <div className="text-xl font-bold text-orange-600" data-id="element-9671">14</div>
              </div>
              <div className="text-center" data-id="element-9672">
                <div className="text-gray-600" data-id="element-9673">Medium</div>
                <div className="text-xl font-bold text-yellow-600" data-id="element-9674">23</div>
              </div>
              <div className="text-center" data-id="element-9675">
                <div className="text-gray-600" data-id="element-9676">Resolved</div>
                <div className="text-xl font-bold text-green-600" data-id="element-9677">23</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* SKU Dependency Network Graph */}
      <Card className="p-6 mb-8" data-id="element-9678">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9679">
          SKU Dependency Network Graph
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-9680">
          Visualization of SKU dependencies and supplier concentration (X:
          Dependencies, Y: Impact Score)
        </p>
        <ResponsiveContainer width="100%" height={450} data-id="element-9681">
          <ScatterChart data-id="element-9682">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9683" />
            <XAxis type="number" dataKey="dependencies" name="Dependencies" tick={{
            fontSize: 12
          }} label={{
            value: 'Number of Dependencies',
            position: 'insideBottom',
            offset: -5
          }} domain={[0, 10]} data-id="element-9684" />
            <YAxis type="number" dataKey="impact" name="Impact Score" tick={{
            fontSize: 12
          }} label={{
            value: 'Impact Score',
            angle: -90,
            position: 'insideLeft'
          }} domain={[0, 100]} data-id="element-9685" />
            <ZAxis type="number" dataKey="suppliers" range={[100, 800]} name="Suppliers" data-id="element-9686" />
            <Tooltip cursor={{
            strokeDasharray: '3 3'
          }} contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} content={({
            active,
            payload
          }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200" data-id="element-9688">
                      <div className="font-bold text-gray-900 mb-2" data-id="element-9689">
                        {data.sku}
                      </div>
                      <div className="text-xs text-gray-600 space-y-1" data-id="element-9690">
                        <div data-id="element-9691">
                          Risk:{' '}
                          <span className={`font-bold ${data.risk === 'Critical' ? 'text-red-600' : data.risk === 'High' ? 'text-orange-600' : data.risk === 'Medium' ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-9692">
                            {data.risk}
                          </span>
                        </div>
                        <div data-id="element-9693">
                          Dependencies:{' '}
                          <span className="font-bold" data-id="element-9694">{data.dependencies}</span>
                        </div>
                        <div data-id="element-9695">
                          Suppliers:{' '}
                          <span className="font-bold" data-id="element-9696">{data.suppliers}</span>
                        </div>
                        <div data-id="element-9697">
                          Impact Score:{' '}
                          <span className="font-bold" data-id="element-9698">{data.impact}</span>
                        </div>
                      </div>
                    </div>;
            }
            return null;
          }} data-id="element-9687" />
            <Legend wrapperStyle={{
            fontSize: '12px'
          }} data-id="element-9699" />
            {['Critical', 'High', 'Medium', 'Low'].map(risk => <Scatter key={risk} name={risk} data={dependencyNetworkData.filter(d => d.risk === risk)} fill={getScatterColor(risk)} data-id="element-9700" />)}
          </ScatterChart>
        </ResponsiveContainer>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-9701">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-9702">
            <div className="text-center" data-id="element-9703">
              <div className="text-gray-600" data-id="element-9704">Critical SKUs</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9705">3</div>
              <div className="text-xs text-gray-500" data-id="element-9706">High dependencies</div>
            </div>
            <div className="text-center" data-id="element-9707">
              <div className="text-gray-600" data-id="element-9708">High Risk SKUs</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-9709">4</div>
              <div className="text-xs text-gray-500" data-id="element-9710">Moderate dependencies</div>
            </div>
            <div className="text-center" data-id="element-9711">
              <div className="text-gray-600" data-id="element-9712">Single Source</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9713">1</div>
              <div className="text-xs text-gray-500" data-id="element-9714">BATT-SYS-H</div>
            </div>
            <div className="text-center" data-id="element-9715">
              <div className="text-gray-600" data-id="element-9716">Avg Dependencies</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9717">4.2</div>
              <div className="text-xs text-gray-500" data-id="element-9718">Per SKU</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Insecure SKUs Table */}
      <Card className="p-6" data-id="element-9719">
        <div className="flex items-center justify-between mb-4" data-id="element-9720">
          <div data-id="element-9721">
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-9722">
              Insecure SKUs List - Detailed Analysis
            </h3>
            <p className="text-sm text-gray-600" data-id="element-9723">
              Critical and high-risk SKUs requiring immediate attention and
              mitigation
            </p>
          </div>
          <div className="flex items-center" data-id="element-9724">
            <AlertTriangleIcon size={20} className="text-red-600 mr-2" data-id="element-9725" />
            <span className="text-sm font-semibold text-red-600" data-id="element-9726">
              10 High-Priority SKUs
            </span>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-9727">
          <table className="w-full" data-id="element-9728">
            <thead data-id="element-9729">
              <tr className="border-b-2 border-gray-300" data-id="element-9730">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9731">
                  SKU Code
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9732">
                  Description
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9733">
                  Category
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9734">
                  Risk Level
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9735">
                  Shortage Risk
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9736">
                  Suppliers
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9737">
                  Lead Time
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9738">
                  Current Stock
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9739">
                  Safety Stock
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9740">
                  Monthly Demand
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9741">
                  Days to Stockout
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9742">
                  Impact Cost
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9743">
                  Mitigation Strategy
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9744">
                  Affected Models
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9745">
              {insecureSKUsData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-9746">
                  <td className="py-3 px-4 text-sm font-mono text-blue-600" data-id="element-9747">
                    {row.sku}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800" data-id="element-9748">
                    {row.description}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9749">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9750">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${getRiskTextColor(row.riskLevel)}`} data-id="element-9751">
                      {row.riskLevel}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9752">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getShortageRiskColor(row.shortageRisk)}`} data-id="element-9753">
                      {row.shortageRisk}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9754">
                    <span className={`text-sm font-bold ${row.supplierCount <= 2 ? 'text-red-600' : row.supplierCount === 3 ? 'text-orange-600' : 'text-green-600'}`} data-id="element-9755">
                      {row.supplierCount}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9756">
                    {row.leadTime}d
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-9757">
                    <span className={`text-sm font-bold ${row.currentStock < row.safetyStock ? 'text-red-600' : 'text-green-600'}`} data-id="element-9758">
                      {row.currentStock}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-700" data-id="element-9759">
                    {row.safetyStock}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-700" data-id="element-9760">
                    {row.monthlyDemand}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9761">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.daysToStockout < 15 ? 'bg-red-100 text-red-700' : row.daysToStockout < 25 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`} data-id="element-9762">
                      {row.daysToStockout}d
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-semibold text-red-600" data-id="element-9763">
                    ₹{row.impactCost} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-9764">
                    {row.mitigation}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 max-w-xs" data-id="element-9765">
                    {row.affectedModels}
                  </td>
                </tr>)}
              {/* Summary Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-9766">
                <td className="py-3 px-4 text-sm text-gray-900" colSpan={5} data-id="element-9767">
                  TOTAL / AVERAGE
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-9768">
                  2.8
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-9769">
                  30d
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-9770">
                  451
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-9771">
                  652
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-9772">
                  548
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-9773">
                  23d
                </td>
                <td className="py-3 px-4 text-right text-sm text-red-600" data-id="element-9774">
                  ₹185 Cr
                </td>
                <td className="py-3 px-4 text-sm text-gray-700" colSpan={2} data-id="element-9775">
                  -
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-9776">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-9777">
            <div className="text-center" data-id="element-9778">
              <div className="text-gray-600" data-id="element-9779">Critical SKUs</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9780">3</div>
            </div>
            <div className="text-center" data-id="element-9781">
              <div className="text-gray-600" data-id="element-9782">High Risk SKUs</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-9783">4</div>
            </div>
            <div className="text-center" data-id="element-9784">
              <div className="text-gray-600" data-id="element-9785">Avg Suppliers</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9786">2.8</div>
            </div>
            <div className="text-center" data-id="element-9787">
              <div className="text-gray-600" data-id="element-9788">Avg Lead Time</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9789">30 days</div>
            </div>
            <div className="text-center" data-id="element-9790">
              <div className="text-gray-600" data-id="element-9791">Below Safety Stock</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9792">10/10</div>
            </div>
            <div className="text-center" data-id="element-9793">
              <div className="text-gray-600" data-id="element-9794">Total Impact</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9795">₹185 Cr</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default MaterialRiskAlertsDashboard;